/**
 * 1-product-variant-options.js
 * Component-specific JavaScript for dynamic variant image updates
 * Updates variant option images dynamically based on currently selected options
 * Works in conjunction with snippets/1-product-variant-options.liquid
 */

class DynamicVariantImages {
  constructor() {
    this.previousFirstOption = null;
    this.init();
  }

  init() {
    // Only listen for direct option selector changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-single-option-selector]')) {
        this.handleOptionChange(event);
      }
    });
    
    // Store initial first option value after a small delay to ensure DOM is ready
    setTimeout(() => {
      this.storePreviousFirstOption();
    }, 100);
  }

  storePreviousFirstOption() {
    // Find the checked/selected first option input
    const firstOptionInputs = document.querySelectorAll('[data-single-option-selector][data-index="option1"]');
    
    if (firstOptionInputs.length > 0) {
      // For radio buttons, find the checked one
      const checkedInput = Array.from(firstOptionInputs).find(input => input.checked);
      if (checkedInput) {
        this.previousFirstOption = checkedInput.value;
      } else {
        // For selects, get the selected value
        this.previousFirstOption = firstOptionInputs[0].value;
      }
    }
  }

  handleOptionChange(event) {
    const changedInput = event.target;
    const optionIndex = this.getOptionIndexFromInput(changedInput);
    
    // Only update images if the first option (index 0) changed
    if (optionIndex === 0) {
      const currentFirstOption = changedInput.value;
      
      // Check if first option actually changed
      if (this.previousFirstOption !== currentFirstOption) {
        // Get current selections from all option selectors
        const productForm = changedInput.closest('[data-product-variants]');
        if (!productForm) return;

        // Get the actual current selections, ensuring we use the new first option value
        const currentSelections = this.getCurrentSelections(productForm, currentFirstOption);
        
        // Update images for 2nd and 3rd options only
        this.updateVariantImagesForLaterOptions(currentSelections);
        
        // Store the new first option value
        this.previousFirstOption = currentFirstOption;
      }
    }
  }

  getCurrentSelections(productForm, newFirstOption = null) {
    const optionSelectors = productForm.querySelectorAll('[data-single-option-selector]');
    const selections = [];
    
    optionSelectors.forEach((selector, index) => {
      if (index === 0 && newFirstOption !== null) {
        // Use the new first option value that was just selected
        selections.push(newFirstOption);
      } else {
        selections.push(selector.value);
      }
    });
    
    return selections;
  }

  getOptionIndexFromInput(input) {
    const indexAttr = input.getAttribute('data-index');
    if (indexAttr) {
      const match = indexAttr.match(/option(\d+)/);
      return match ? parseInt(match[1]) - 1 : null;
    }
    return null;
  }

  updateVariantImagesForLaterOptions(currentSelections) {
    // Find all variant option images that need updating, but exclude first option
    const variantButtons = document.querySelectorAll('[data-variant-option-image]');
    
    if (!variantButtons.length) return;

    // Get product variants data
    const productJsonElement = document.querySelector('[data-product-json]');
    if (!productJsonElement) return;

    let productData;
    try {
      productData = JSON.parse(productJsonElement.textContent);
    } catch (e) {
      console.warn('Could not parse product data for dynamic variant images');
      return;
    }

    variantButtons.forEach(button => {
      const optionPosition = this.getOptionPositionFromButton(button);
      
      // Only update images for options after the first one (position > 0)
      if (optionPosition > 0) {
        this.updateButtonImage(button, currentSelections, productData.variants);
      }
    });
  }

  updateButtonImage(button, currentSelections, variants) {
    // Get the option position and value for this button
    const optionPosition = this.getOptionPositionFromButton(button);
    const optionValue = this.getOptionValueFromButton(button);
    
    if (optionPosition === null || !optionValue) return;

    // Find the best matching variant
    const bestVariant = this.findBestMatchingVariant(
      variants, 
      optionPosition, 
      optionValue, 
      currentSelections
    );

    if (bestVariant && bestVariant.featured_media) {
      this.replaceButtonImage(button, bestVariant.featured_media);
    }
  }

  getOptionPositionFromButton(button) {
    // Find the parent selector wrapper to get option position
    const selectorWrapper = button.closest('[data-option-position]');
    if (selectorWrapper) {
      return parseInt(selectorWrapper.getAttribute('data-option-position')) - 1;
    }
    
    // Fallback: find position from input attributes
    const input = button.querySelector('input[data-index]');
    if (input) {
      const indexAttr = input.getAttribute('data-index');
      const match = indexAttr.match(/option(\d+)/);
      return match ? parseInt(match[1]) - 1 : null;
    }
    
    return null;
  }

  getOptionValueFromButton(button) {
    const input = button.querySelector('input');
    return input ? input.value : null;
  }

  findBestMatchingVariant(variants, optionPosition, optionValue, currentSelections) {
    let bestMatch = null;
    let fallbackMatch = null;

    for (const variant of variants) {
      // Must match the current option value
      if (variant.options[optionPosition] !== optionValue) continue;

      // Store first match as fallback
      if (!fallbackMatch) {
        fallbackMatch = variant;
      }

      // Check if this variant perfectly matches current selections
      let isPerfectMatch = true;
      for (let i = 0; i < optionPosition; i++) {
        if (currentSelections[i] && variant.options[i] !== currentSelections[i]) {
          isPerfectMatch = false;
          break;
        }
      }

      if (isPerfectMatch) {
        bestMatch = variant;
        break; // Found perfect match, stop looking
      }
    }

    return bestMatch || fallbackMatch;
  }

  replaceButtonImage(button, media) {
    const optionImageContainer = button.querySelector('.option-image');
    if (!optionImageContainer) return;

    const existingImg = optionImageContainer.querySelector('img');
    if (!existingImg) return;

    // Create new image with updated src
    const newImg = existingImg.cloneNode(true);
    
    // Update image source for the new variant
    if (media.preview_image && media.preview_image.src) {
      const baseUrl = media.preview_image.src;
      
      try {
        // Update main src (height: 60)
        newImg.src = this.createImageUrl(baseUrl, { height: 60 });
        
        // Update srcset if it exists
        if (newImg.srcset) {
          const newSrcset = this.createResponsiveSrcset(baseUrl, [60, 90, 120, 150, 180]);
          newImg.srcset = newSrcset;
        }
        
        // Update alt text
        newImg.alt = media.alt || '';
        
        // Replace the image
        existingImg.replaceWith(newImg);
      } catch (error) {
        console.warn('Error updating variant image:', error, 'Media object:', media);
        // Don't replace the image if there's an error
      }
    } else {
      console.warn('Invalid media object or missing preview_image.src:', media);
    }
  }

  createImageUrl(baseUrl, params) {
    if (!baseUrl || typeof baseUrl !== 'string') {
      throw new Error('Invalid baseUrl provided to createImageUrl');
    }
    
    let url;
    
    // Handle both absolute URLs and relative paths
    try {
      url = new URL(baseUrl);
    } catch (e) {
      try {
        // If baseUrl is a relative path, make it absolute
        url = new URL(baseUrl, window.location.origin);
      } catch (e2) {
        // If still failing, try with https protocol
        const cleanUrl = baseUrl.startsWith('//') ? 'https:' + baseUrl : baseUrl;
        url = new URL(cleanUrl, window.location.origin);
      }
    }
    
    // Remove existing size parameters
    url.searchParams.delete('width');
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    
    // Add new parameters
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    
    return url.toString();
  }

  createResponsiveSrcset(baseUrl, widths) {
    return widths.map(width => {
      const url = this.createImageUrl(baseUrl, { width: width });
      return `${url} ${width}w`;
    }).join(', ');
  }
}

class SelectedVariantSuboptions {
  constructor() {
    this.refreshTimers = new WeakMap();
    this.productData = new WeakMap();
    this.init();

    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-single-option-selector]')) {
        const productSelectors = event.target.closest('[data-product-variants]');
        if (productSelectors) this.scheduleRefresh(productSelectors);
      }
    });

    document.addEventListener('shopify:section:load', (event) => {
      this.init(event.target);
    });
  }

  init(root = document) {
    root.querySelectorAll('[data-product-variants]').forEach((productSelectors) => {
      this.scheduleRefresh(productSelectors);
    });
  }

  scheduleRefresh(productSelectors) {
    const previousTimer = this.refreshTimers.get(productSelectors);
    if (previousTimer) clearTimeout(previousTimer);

    this.placeSuboptions(productSelectors);

    const requestFrame = window.requestAnimationFrame || ((callback) => setTimeout(callback, 0));
    requestFrame(() => this.placeSuboptions(productSelectors));

    const timer = setTimeout(() => {
      this.placeSuboptions(productSelectors);
      this.refreshTimers.delete(productSelectors);
    }, 50);

    this.refreshTimers.set(productSelectors, timer);
  }

  placeSuboptions(productSelectors) {
    const mainWrapper = productSelectors.querySelector('[data-option-position="1"]');
    const mainButtons = mainWrapper?.querySelector('[data-variant-buttons]');
    if (!mainWrapper || !mainButtons) return;

    const subWrappers = Array.from(productSelectors.querySelectorAll('[data-option-position]')).filter((child) => {
      return child !== mainWrapper && child.getAttribute('data-option-position') !== '1';
    });

    if (!subWrappers.length) return;

    const checkedMainOption = mainWrapper.querySelector('[data-single-option-selector]:checked');
    const mainOptionValue = checkedMainOption?.value || mainWrapper.querySelector('[data-single-option-selector]')?.value;
    this.updateSuboptionVisibility(productSelectors, subWrappers, mainOptionValue);

    const selectedButton = checkedMainOption?.closest('.radio__button, .swatch__button, tooltip-component');
    const mainOptionButtons = Array.from(mainButtons.children).filter((child) => {
      return child.matches('.radio__button, .swatch__button, tooltip-component');
    });

    if (!selectedButton || selectedButton.parentNode !== mainButtons || !mainOptionButtons.length) return;

    if (window.theme?.isMobile?.()) {
      let insertAfter = mainWrapper;
      subWrappers.forEach((subWrapper) => {
        subWrapper.classList.add('selector-wrapper--subvariants');
        insertAfter.after(subWrapper);
        insertAfter = subWrapper;
      });
      return;
    }

    const selectedIndex = mainOptionButtons.indexOf(selectedButton);
    if (selectedIndex < 0) return;

    const gridColumns = getComputedStyle(mainButtons).gridTemplateColumns;
    const columnCount = gridColumns && gridColumns !== 'none' ? gridColumns.split(' ').length : 1;
    const rowEndIndex = Math.min(
      mainOptionButtons.length - 1,
      selectedIndex + (columnCount - 1 - (selectedIndex % columnCount))
    );

    let insertAfter = mainOptionButtons[rowEndIndex] || selectedButton;
    subWrappers.forEach((subWrapper) => {
      subWrapper.classList.add('selector-wrapper--subvariants');
      insertAfter.after(subWrapper);
      insertAfter = subWrapper;
    });
  }

  updateSuboptionVisibility(productSelectors, subWrappers, mainOptionValue) {
    const productData = this.getProductData(productSelectors);
    const currentSelections = this.getCurrentSelections(productSelectors);
    const hideOosVariants = productSelectors.getAttribute('data-hide-oos-variants') === 'true';
    currentSelections[0] = mainOptionValue;

    if (!productData || !mainOptionValue) {
      subWrappers.forEach((subWrapper) => this.toggleSuboptionButtons(subWrapper, () => true));
      return;
    }

    subWrappers.forEach((subWrapper) => {
      const optionPosition = parseInt(subWrapper.getAttribute('data-option-position'), 10) - 1;

      this.toggleSuboptionButtons(subWrapper, (optionValue) => {
        return productData.variants.some((variant) => {
          return this.variantMatchesSelections(variant, currentSelections, optionPosition, optionValue, hideOosVariants);
        });
      });
    });
  }

  getCurrentSelections(productSelectors) {
    const selections = [];
    const optionInputs = productSelectors.querySelectorAll('[data-single-option-selector]');

    optionInputs.forEach((optionInput) => {
      if (optionInput.type === 'radio' && !optionInput.checked) return;

      const index = this.getOptionIndex(optionInput);
      if (index === null) return;

      selections[index] = optionInput.value;
    });

    return selections;
  }

  getOptionIndex(optionInput) {
    const indexAttr = optionInput.getAttribute('data-index');
    if (!indexAttr) return null;

    const match = indexAttr.match(/option(\d+)/);
    return match ? parseInt(match[1], 10) - 1 : null;
  }

  variantMatchesSelections(variant, selections, optionPosition, optionValue, hideOosVariants = false) {
    if (hideOosVariants && !variant.available) return false;
    if (variant.options[optionPosition] !== optionValue) return false;

    for (let index = 0; index < optionPosition; index++) {
      if (selections[index] && variant.options[index] !== selections[index]) {
        return false;
      }
    }

    return true;
  }

  toggleSuboptionButtons(subWrapper, isVisible) {
    const optionInputs = subWrapper.querySelectorAll('[data-single-option-selector], [data-popout-option]');

    optionInputs.forEach((optionInput) => {
      const optionValue = optionInput.value || optionInput.getAttribute('data-value');
      const optionElement = optionInput.closest('.radio__button, .swatch__button, tooltip-component, .select-popout__item');
      if (!optionElement || !optionValue) return;

      optionElement.hidden = !isVisible(optionValue);
    });
  }

  getProductData(productSelectors) {
    if (this.productData.has(productSelectors)) {
      return this.productData.get(productSelectors);
    }

    const productRoot = productSelectors.closest('[data-product]');
    const searchRoot = productRoot?.parentElement || productSelectors.closest('[data-section-id]') || document;
    const productJsonElement = searchRoot.querySelector('[data-product-json]') || document.querySelector('[data-product-json]');
    if (!productJsonElement) return null;

    try {
      const parsedProductData = JSON.parse(productJsonElement.textContent);
      this.productData.set(productSelectors, parsedProductData);

      return parsedProductData;
    } catch (error) {
      console.warn('Could not parse product data for subvariant visibility');
      return null;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DynamicVariantImages();
    new SelectedVariantSuboptions();
  });
} else {
  new DynamicVariantImages();
  new SelectedVariantSuboptions();
}
